import { useMutation } from '@apollo/client';
import { faPencil, faArrowRotateRight, faTrashCan, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';

import {
    DELETE_ORDERWISE_INTEGRATION,
    GET_ORDERWISE_INTEGRATIONS,
    TRIGGER_ORDERWISE_INTEGRATION_MASTERDATA_UPDATE,
} from './queries.ts';
import type { OrderwiseIntegration } from './types.ts';
import { withIcon } from '../../../core/components/Icon.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { url } from '../../../core/utils/link.ts';
import { CUSTOMER_INTEGRATION_ORDERWISE_EDIT_PATH } from '../../constants.ts';

const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const RefreshIcon = withIcon(faArrowRotateRight);
const LoadingIcon = withIcon(faSpinnerThird);

export const OrderwiseRow = ({
    integration,
    refetch,
}: {
    integration: OrderwiseIntegration;
    refetch: () => Promise<any>;
}) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [deleteIntegration] = useMutation(DELETE_ORDERWISE_INTEGRATION, {
        refetchQueries: [GET_ORDERWISE_INTEGRATIONS],
    });
    const handleDeleteIntegration = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            publishToast({ description: t('orderwise.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('orderwise.deleteError'), status: 'error' });
        }
    };

    const [updateMasterDataIntegration] = useMutation(TRIGGER_ORDERWISE_INTEGRATION_MASTERDATA_UPDATE);
    const handleUpdateMasterDataIntegration = async () => {
        try {
            await updateMasterDataIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('orderwise.updateMasterDataSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('orderwise.updateMasterDataError'), status: 'error' });
        }
    };

    const orderChannels = integration ? [...integration.orderChannels] : [];
    const rfqChannels = integration ? [...integration.rfqChannels] : [];

    return (
        <Table.Row>
            <Table.Cell>{integration.name}</Table.Cell>
            <Table.Cell>
                <div className="flex gap-1.5 flex-wrap">
                    {orderChannels.map((channel) => (
                        <span
                            key={channel.id}
                            className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                        >
                            {channel.name}
                        </span>
                    ))}
                </div>
            </Table.Cell>
            <Table.Cell>
                <div className="flex gap-1.5 flex-wrap">
                    {rfqChannels.map((channel) => (
                        <span
                            key={channel.id}
                            className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                        >
                            {channel.name}
                        </span>
                    ))}
                </div>
            </Table.Cell>
            <Table.Cell>
                {integration.masterDataUpdateActive ? (
                    <span className="flex gap-2 items-center animate-pulse">
                        <span className="text-content-success-strong">
                            <LoadingIcon spin />
                        </span>
                        <span>
                            {`${t('orderwise.masterDataUpdateInProgress')} ${(integration.fetchProgress * 100).toFixed(2)}%`}
                        </span>
                    </span>
                ) : integration.masterDataUpdateError ? (
                    <div className="flex flex-col gap-1">
                        <span className="text-content-danger-strong">{t('orderwise.masterDataUpdateError')}</span>
                        <span className="text-content-medium text-sm whitespace-pre-line">
                            {integration.masterDataUpdateError}
                        </span>
                    </div>
                ) : (
                    <span>
                        {t('orderwise.lastMasterDataUpdate', {
                            updatedAt: format(new Date(integration.masterDataUpdatedAt), 'do MMMM yyyy, HH:mm'),
                        })}
                    </span>
                )}
            </Table.Cell>
            <Table.Cell>{new Date(integration.createdAt).toLocaleString()}</Table.Cell>
            <Table.Cell sticky="right">
                <div className="flex gap-2">
                    <RowControlIconButton onClick={handleUpdateMasterDataIntegration}>
                        <RefreshIcon />
                    </RowControlIconButton>

                    <RowControlIconButton asChild>
                        <Link
                            to={url(CUSTOMER_INTEGRATION_ORDERWISE_EDIT_PATH, {
                                integrationId: integration.id,
                            })}
                            className="!text-content-strong hover:!text-content-strong"
                        >
                            <EditIcon />
                        </Link>
                    </RowControlIconButton>

                    <RowControlIconButton
                        className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                        onClick={handleDeleteIntegration}
                    >
                        <DeleteIcon />
                    </RowControlIconButton>
                </div>
            </Table.Cell>
        </Table.Row>
    );
};
